import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Card, CardContent, CardHeader, Container, Grid, LinearProgress, Stack, Typography } from "@mui/material";

import { ActionTypes } from "../../../../constants";
import { getPDPDetails, updatePDP } from "../../../../actions/rmsActions";
import { getPDP, getError, getUser, getRmsAppCode } from "../../../../reducers";

import AlertMessage from "../../../Common/Alert/AlertMessage";
import BreadcrumbBuilder from "../BreadcrumbBuilder";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import DevelopmentNeedTable from "../DevelopmentNeed/DevelopmentNeedTable";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ErrorHandler from "../../../Errors/ErrorHandler";
import PDPDetails from "./PDPDetails";
import PDPFormDialog from "./PDPFormDialog";
import PORTAvatar from "../PORTAvatar";
import RenderDate from "../../../Common/Date/RenderDate";

import BreadcrumbBack from "../../../Common/Breadcrumbs/BreadcrumbBack";


const PDP = ({ match }) => {
    const dispatch = useDispatch();
    const pdpId = Number(match.params.id);
    const pdp = useSelector((state) => getPDP(state, pdpId));
    const error = useSelector((state) => getError(state, ActionTypes.GET_PDP_SUCCESS));
    const user = useSelector(getUser);

    const appCode = useSelector(getRmsAppCode);

    useEffect(() => {
        if (!!appCode) {
            const params = !match.params.appraiseeUUID ? null : {model: "review", appraiseeUUID: match.params.appraiseeUUID};
            dispatch(getPDPDetails(pdpId, appCode, params));
        }
    }, [dispatch, pdpId, match.params.appraiseeUUID, appCode]);

    const [openDialog, setOpenDialog] = useState(null);
    const handleCloseDialog = () => setOpenDialog(null);
    const handleUpdate = (id, params) => {
        dispatch(updatePDP(appCode, id, params));
        handleCloseDialog();
    };

    const canUpdate = pdp?.canUpdate ?? false;
    const canAdd = pdp?.canAdd ?? false;
    let canComplete = false;

    const isAppraiser = !!pdp?.appraiser?.find(({ uuid }) => uuid === user?.uuid);

    const location = useLocation();
    const queryString = new URLSearchParams(location.search);
    const refererPath = queryString.get("rp");

    if (pdp?.status === "open" && isAppraiser) {
        if (pdp?.developmentNeeds?.length > 0) {
            canComplete = pdp.developmentNeeds.every((item) => ["completed", "deferred", "cancelled"].includes(item.status));
        } else {
            canComplete = true;
        }
    }

    if (!!error && (error.errorCode === 404 || error.errorCode === 500)) {
        return (
            <ErrorHandler error={error} breadCrumb={<BreadcrumbBuilder />} />
        );
    }

    if (!pdp) return <LinearProgress />;

    return (
        <Container className="content">
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    {refererPath?.length > 0 ? (
                        <BreadcrumbBack path={refererPath} />
                    ) : (
                        <BreadcrumbBuilder />
                    )}
                </Grid>
                <Grid item xs={12}>
                    <Card className="card">
                        <CardHeader
                            disableTypography={true}
                            avatar={
                                <PORTAvatar
                                    type={"pdp"}
                                    badge={pdp?.status === "completed" ? "checked" : ""}
                                />
                            }
                            action={
                                <Stack
                                    direction="row"
                                    justifyContent="flex-start"
                                    alignItems="center"
                                    spacing={2}
                                    sx={{ padding: "8px" }}
                                >
                                    {canUpdate && (
                                        <Button
                                            variant="contained"
                                            color="neutral"
                                            disableElevation={true}
                                            aria-label="edit PDP"
                                            onClick={() => setOpenDialog("update")}
                                            startIcon={<EditOutlinedIcon />}
                                        >
                                            Edit
                                        </Button>
                                    )}
                                    {canComplete && (
                                        <Button
                                            variant="contained"
                                            color="success"
                                            disableElevation={true}
                                            aria-label="Complete PDP"
                                            onClick={() => setOpenDialog("complete")}
                                            endIcon={<CheckOutlinedIcon style={{ color: "#FFF" }}/>}
                                        >
                                            <Typography variant={"span"} sx={{ color: "#FFF" }}>
                                                Complete
                                            </Typography>
                                        </Button>
                                    )}
                                </Stack>
                            }
                            title={
                                <Typography component="h2" className="card-header-title">
                                    {pdp.title}
                                </Typography>
                            }
                            subheader={
                                <Typography component="span" className="card-header-subtitle">
                                    {!!pdp?.dateStart && (
                                        <RenderDate date={pdp.dateStart} />
                                    )}
                                    &nbsp;-&nbsp;
                                    {!!pdp?.dateEnd && (
                                        <RenderDate date={pdp.dateEnd} />
                                    )}
                                </Typography>
                            }
                        />
                        <CardContent>
                            <Box className="card-content">
                                <PDPDetails
                                    owner={pdp?.owner}
                                    appraiser={pdp?.appraiser}
                                    isAppraiser={isAppraiser}
                                />
                            </Box>
                        </CardContent>
                        <CardContent>
                            <Box className="card-content">
                                <DevelopmentNeedTable
                                    canUpdate={canUpdate}
                                    canAdd={canAdd}
                                    pdpId={pdp?.id}
                                    items={pdp?.developmentNeeds ?? []}
                                    isAppraiser={isAppraiser}
                                    appraiseeUUID={match.params?.appraiseeUUID || null}
                                    appCode={appCode}
                                />
                            </Box>
                        </CardContent>
                        {pdp?.description && (
                            <CardContent>
                                <Box className="card-content">
                                    <Typography variant={"body1"}>
                                        {pdp?.description}
                                    </Typography>
                                </Box>
                            </CardContent>
                        )}
                        {isAppraiser && pdp.status === "open" && (
                            <CardContent>
                                <Box className="card-content">
                                    <AlertMessage
                                        severity="info"
                                        message="To mark this PDP as completed, all development needs must
                                                have one of the following statuses [ completed, deferred or cancelled ]."
                                    />
                                </Box>
                            </CardContent>
                        )}
                    </Card>
                </Grid>
            </Grid>

            {openDialog === "update" && (
                <PDPFormDialog
                    open={true}
                    formTitle="Edit PDP"
                    submitLabel="Save"
                    canComplete={false}
                    data={pdp}
                    onSave={(params) => handleUpdate(pdp.id, params)}
                    onClose={handleCloseDialog}
                />
            )}

            {openDialog === "complete" && (
                <PDPFormDialog
                    open={true}
                    formTitle="Complete PDP"
                    submitLabel="Complete"
                    canComplete={canComplete}
                    data={pdp}
                    onSave={(params) => handleUpdate(pdp.id, params)}
                    onClose={handleCloseDialog}
                />
            )}
        </Container>
    );
};

export default PDP;
